/**
 * Bootstrap components initialization module
 * Handles initialization of Bootstrap components like tooltips
 */

import { logError } from '../core/error-handler.js';

/**
 * Initialize Bootstrap tooltips in a specific container
 * @param {jQuery|HTMLElement|string} container - Container to search for tooltips
 */
export function initializeTooltipsInContainer(container) {
    try {
        const $container = typeof container === 'string' ? $(container) : $(container);
        $container.find('[data-toggle="tooltip"]').each((index, element) => {
            // Destroy existing tooltip if any to avoid duplicates
            const $el = $(element);
            if ($el.data('bs.tooltip')) {
                $el.tooltip('dispose');
            }
            $el.tooltip();
        });
    } catch (error) {
        logError(error, { context: 'Tooltip initialization in container' });
    }
}

/**
 * Initialize Bootstrap tooltips globally
 * Sets up tooltip initialization for:
 * - Initial page load
 * - Dynamically added content (via MutationObserver)
 * - Kendo Grid dataBound events (after grid refresh)
 *
 * @function initializeBootstrapTooltips
 * @description Configures global Bootstrap tooltip initialization with support for:
 * - Initial page tooltips
 * - Dynamic content detection via MutationObserver
 * - Kendo Grid integration for tooltips in grid cells
 */
export function initializeBootstrapTooltips() {
    try {
        // Initialize all tooltips on the page initially
        initializeTooltipsInContainer(document);

        // Use MutationObserver for dynamic content (more reliable than DOMNodeInserted)
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    if (node.nodeType === 1) {
                        // Element node
                        const $node = $(node);
                        // Initialize tooltips in the added node and its children
                        initializeTooltipsInContainer($node);
                    }
                });
            });
        });

        // Observe the entire document for changes
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Initialize tooltips when Kendo Grid data is bound (after grid refresh)
        $(document).on('dataBound', '.k-grid', event => {
            const $grid = $(event.currentTarget);
            const grid = $grid.data('kendoGrid');
            if (grid) {
                // Initialize tooltips in the grid content area
                const $gridContent = $grid.find('.k-grid-content, .k-grid-content-locked');
                initializeTooltipsInContainer($gridContent);
            }
        });
    } catch (error) {
        logError(error, { context: 'Bootstrap tooltips initialization' });
    }
}
